import { ChangeDetectionStrategy, Component, ElementRef, afterEveryRender, booleanAttribute, computed, inject, input, signal } from '@angular/core';
import { RadIconComponent, RadIconName } from '../icon/icon.component';
import { RadSpinnerComponent } from '../spinner/spinner.component';
import { RadInteractiveDirective } from '../interactive/interactive.directive';

// Colour is the only thing that varies between the kinds, and each is a set of role tokens — so the
// variants are data, looked up, not a style block each. Adding one is a line here. Every value is a
// theme role (brand, danger, surface, border, text), never a self-mixed colour.
const VARIANTS = {
    primary: { background: 'var(--color-primary-color)', color: 'var(--color-primary-contrast)', border: 'transparent' },
    secondary: { background: 'var(--rad-surface-raised-background-color)', color: 'var(--rad-text-default-font-color)', border: 'var(--rad-border-strong-color)' },
    ghost: { background: 'transparent', color: 'var(--color-primary-color)', border: 'transparent' },
    danger: { background: 'var(--color-danger-color)', color: 'var(--color-danger-contrast)', border: 'transparent' },
    success: { background: 'var(--color-success-color)', color: 'var(--color-success-contrast)', border: 'transparent' },
    inverse: { background: 'transparent', color: 'var(--color-primary-contrast)', border: 'transparent' },
} as const;

// Size is a fixed control height, not padding around content — so a label, an icon or a spinner
// never changes the button's size. Each entry is a height token and the horizontal padding a text
// label sits in. Adding a larger size is a line here plus a --rad-control-height-lg token.
const SIZES = {
    sm: { height: 'var(--rad-control-height-sm)', padding: 'var(--rad-space-4)' },
    md: { height: 'var(--rad-control-height-md)', padding: 'var(--rad-space-5)' },
} as const;

// The selector is a native <button>, so the element is a real control — keyboard, focus, disabled
// and form participation are the browser's, not ours to rebuild. hostDirectives adds the shared
// [rad-interactive] treatment (the hover shadow, brand ring, press and focus ring from States § 2)
// rather than hand-rolling any of it. The label is projected; icon-only is a button with no label
// and a native aria-label. There is no output — the native click is the event.
@Component({
    selector: 'button[rad-button]',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RadIconComponent, RadSpinnerComponent],
    hostDirectives: [RadInteractiveDirective],
    host: {
        '[disabled]': 'disabled() || loading()',
        '[attr.aria-busy]': 'loading() || null',
        '[style.height]': 'sizing().height',
        '[style.padding]': 'padding()',
        '[style.aspect-ratio]': 'iconOnly() ? "1" : null',
        '[style.background-color]': 'palette().background',
        '[style.color]': 'palette().color',
        '[style.border-color]': 'palette().border',
    },
    template: `
        @if (loading()) {
            <rad-spinner size="inline" />
        } @else if (icon() && iconPosition() === 'start') {
            <rad-icon [name]="icon()" size="inline" [style.color]="iconColorStyle()" />
        }
        <ng-content />
        @if (!loading() && icon() && iconPosition() === 'end') {
            <rad-icon [name]="icon()" size="inline" [style.color]="iconColorStyle()" />
        }
        @if (dropdown()) {
            <rad-icon class="rad-button__caret" name="fa:chevron-down" size="inline" />
        }
    `,
    styles: `
        :host {
            box-sizing: border-box;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: var(--rad-space-3);
            border: var(--rad-border-width) solid;
            border-radius: var(--rad-radius-2);
            font-family: var(--main-font-family);
            font-size: var(--rad-text-md-font-size);
            font-weight: var(--rad-text-strong-font-weight);
            line-height: 1;
        }
        // The caret turns over while the panel is open. aria-expanded is the attribute whatever
        // opened the panel already sets, so the button reflects a state it is told rather than
        // knowing anything about menus.
        .rad-button__caret {
            transition: transform var(--rad-motion-fast) var(--rad-motion-ease-standard);
        }
        :host([aria-expanded='true']) .rad-button__caret {
            transform: rotate(180deg);
        }
        :host(:disabled:not([aria-busy])) {
            opacity: 0.5;
            cursor: not-allowed;
        }
        :host(:disabled):hover {
            box-shadow: none;
        }
    `,
})
export class RadButtonComponent {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    /** The visual role: `primary` for the one main action, `secondary` beside it, `ghost` for a low-emphasis action, `danger` for a destructive one, `success` for one that confirms, `inverse` for a control on a filled brand band. */
    readonly variant = input<keyof typeof VARIANTS>('primary');

    /** The control height, `sm` or `md`. It is a fixed height, so content never resizes the button; an icon-only button squares to it. */
    readonly size = input<keyof typeof SIZES>('md');

    /** An optional icon reference shown with the label — a vocabulary name, or an `fa:` / `svg:` token. Omit the label, keeping a native `aria-label`, for an icon-only button. */
    readonly icon = input<RadIconName>();

    /** Which side the icon sits: `start` leads the label, `end` trails it — a forward arrow on a navigational action. */
    readonly iconPosition = input<'start' | 'end'>('start');

    /** A theme colour for the icon, when it should read apart from the label; `none` inherits the label's colour. */
    readonly iconColor = input<'none' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger'>('none');

    /** Draw a caret after the label, marking a button that opens a menu. It turns over while the menu is open. */
    readonly dropdown = input(false, { transform: booleanAttribute });

    /** Show a spinner and block the action while work is in flight. It also disables the button and marks it `aria-busy`. */
    readonly loading = input(false, { transform: booleanAttribute });

    /** Disable the button. Show the reason beside it, or don't render the button at all — see [States § 4](?path=/docs/rad-ui-standards-states--docs). */
    readonly disabled = input(false, { transform: booleanAttribute });

    protected readonly palette = computed(() => VARIANTS[this.variant()]);
    protected readonly sizing = computed(() => SIZES[this.size()]);

    // The icon's colour is the consumer's, not the variant's — a toolbar wants the brand glyph on a
    // secondary button, and nothing about being secondary decides that. null leaves the icon on the
    // label's colour rather than writing one, so an unset button inherits as it always has.
    protected readonly iconColorStyle = computed(() => this.iconColor() === 'none' ? null : 'var(--color-' + this.iconColor() + '-color)');

    // A button holding one glyph and nothing else is icon-only: it squares up (aspect-ratio 1) and
    // drops its horizontal padding, so the glyph sits centred in a box the height of the control.
    // A caret is a second glyph, so a dropdown with no label is not icon-only — squaring that one
    // fits two glyphs into the width of one and leaves neither any room.
    //
    // The label is read after every render rather than once the content has arrived, because a
    // screen drops it on a mobile page and puts it back on a desktop one. The glyph contributes no
    // text of its own, so empty text means no label. Writing the signal the value it already holds
    // changes nothing, so this cannot drive itself round.
    private readonly labelled = signal(true);
    protected readonly iconOnly = computed(() => !this.labelled() && !this.dropdown());
    protected readonly padding = computed(() => this.iconOnly() ? '0' : '0 ' + this.sizing().padding);

    constructor() {
        afterEveryRender({ read: () => this.labelled.set((this.host.textContent ?? '').trim() !== '') });
    }
}
